import { Grid, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  IconButton,
  Icons,
  Link,
  MaterialPalette,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useHistory } from 'react-router';

import { LoginSteps } from '../../utilities/enums';

interface Props {
  goToStep: (step: LoginSteps) => void;
  email: string;
  attemptUserSignIn: (password: string) => Promise<string>;
  isLoggingIn: boolean;
}

const useStyles = makeStyles({
  nextBtn: { width: '100%' },
  root: {
    '& .signupLink': {
      fontWeight: 700,
      marginLeft: 4,
    },
    '& .signupLink:hover': {
      textDecoration: 'none',
    },
  },
  diffUserLink: {
    margin: '10px 0px 0px 0px',
    '& .forgotPasswordLink': {
      fontWeight: 700,
    },
    '& .forgotPasswordLink:hover': {
      textDecoration: 'none',
    },
  },
  needAccountStyle: {
    color: 'rgba(255, 255, 255, 0.6)',
    display: 'inline',
  },
  signinGrid: {
    marginTop: 24,
    marginBottom: 24,
    '& #signinButtonDisabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.48)',
      borderRadius: '4px',
    },
    '& #signinButton': {
      borderRadius: '4px',
    },
  },
  signinButtonLabel: {
    color: MaterialPalette.common.black,
  },
});

const LoginFlowPassword = forwardRef(
  ({ goToStep, email, attemptUserSignIn, isLoggingIn }: Props, ref) => {
    const history = useHistory();
    const classes = useStyles();
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (event: React.FocusEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setPassword(value);
    };

    const handleClickShowPassword = () => {
      setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    const resetFormValidation = () => {
      setPasswordError('');
    };

    const validateForm = (): boolean => {
      let valid = true;

      if (password === '' || email === '') {
        setPasswordError('User Name and Password Required.');
        valid = false;
      }
      return valid;
    };

    const signInUser = async () => {
      if (isLoggingIn) return;
      resetFormValidation();

      if (!validateForm()) {
        return;
      }

      const result = await attemptUserSignIn(password);
      if (result !== '') {
        setPasswordError(result);
      }
    };

    useImperativeHandle(ref, () => ({
      signInFormSubmit() {
        signInUser();
      },
    }));

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        signInUser();
      }
    };

    const goToForgorPassword = () => {
      goToStep(LoginSteps.FORGOT_PASSWORD);
    };

    return (
      <Grid item container xs={12} className={classes.root}>
        <TextField
          id="Password"
          key="Password"
          name="Password"
          noMinHeight
          label="Password"
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          error={passwordError !== ''}
          errorText={passwordError}
          type={showPassword ? 'text' : 'password'}
          value={password}
          size="small"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                tabIndex={-1}
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? (
                  <Icons.EyeFeather strokeWidth={2} />
                ) : (
                  <Icons.EyeOffFeather strokeWidth={2} />
                )}
              </IconButton>
            </InputAdornment>
          }
          inputProps={{ 'data-testid': 'password-input' }}
        />

        <div className={classes.diffUserLink}>
          <Link
            onClick={goToForgorPassword}
            className="forgotPasswordLink"
            to=""
            color="primary"
            data-cy="forgotPassword"
          >
            Forgot Password?
          </Link>
        </div>

        <Grid item container xs={12} justifyContent="center" className={classes.signinGrid}>
          <Grid className={classes.nextBtn}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={signInUser}
              isLoading={isLoggingIn}
              data-cy="loginAsUser"
              disabled={!password}
              id={!password ? 'signinButtonDisabled' : 'signinButton'}
              noMinHeight
              data-testid="login-button"
            >
              <Typography fontWeight={700} className={classes.signinButtonLabel}>
                Sign In
              </Typography>
            </Button>
          </Grid>
        </Grid>

        <Grid item container xs={12} md={12} justifyContent="center" alignItems="baseline">
          <Typography className={classes.needAccountStyle} fontWeight={700}>
            Need an account?
          </Typography>
          <Link
            to=""
            onClick={() => history.push('/signup')}
            color="primary"
            className="signupLink"
          >
            Signup
          </Link>
        </Grid>
      </Grid>
    );
  },
);

export default LoginFlowPassword;
